import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import * as yup from "yup";
import { toast } from "react-toastify";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, useForm } from "react-hook-form";

import { PanelWidth } from "../../../../components/panel/Panel";
import { useConvertScrToD2TMutation, useTheViewDataViewQuery, useViewerQuery } from "../../../../graphql/schema";
import { Button } from "../../../../components/button/Button";
import { Color } from "../../../../services/buttonLinkConstants";
import { Layout } from "../../../../components/layout/Layout";
import { DataFraction } from "../../../../components/data-fraction/DataFraction";
import DateTime from "../../../../components/date-time/DateTime";
import { Modal, ModalKind } from "../../../../components/modal/Modal";
import { showGraphqlValidationErrors } from "../../../../services/showGraphqlValidationErrors";
import Field from "../../../../components/field/Field";
import { CopyToClipboard } from "../../../../components/copy-to-clipboard/CopyToClipBoard";
import { IconBind } from "../../../../components/icon/IconBind";
import { Section } from "../../../../components/section/Section";
import { Loader } from "../../../../components/loader/Loader";
import { Heading } from "../../../../components/heading/Heading";

import styles from "./the-view-scr-burn-view.module.scss";

export interface TheViewScrToBrokerProps {
  className?: string;
}

interface BurnSCRD2TFormInfo {
  totalAmount: string;
  name: string;
  email: string;
  convertBalance?: boolean;
  // username: string;
  fconfirm: boolean;
  accountOpened: boolean;
  acknowledgement: boolean;
  acknowledgementTrading: boolean;
}

const burnSCRD2TValidationSchema: yup.SchemaOf<BurnSCRD2TFormInfo> = yup.object().shape({
  totalAmount: yup.string().required("Amount is required"),
  name: yup.string().required("Name is required"),
  email: yup.string().email().required("Email is required"),
  convertBalance: yup.boolean().optional(),
  // username: yup.string().required("Username is required"),
  fconfirm: yup
    .boolean()
    .oneOf([true], "Please check that you understand the terms")
    .required("Please check that you understand the terms"),
  accountOpened: yup
    .boolean()
    .oneOf([true], "Please check that you have opened TAG Markets account")
    .required("Please check that you have opened TAG Markets account"),
  acknowledgement: yup
    .boolean()
    .oneOf([true], "Please check that you understand that the burn and send is final and can not be reversed.")
    .required("Please check that you understand that the burn and send is final and can not be reversed."),
  acknowledgementTrading: yup
    .boolean()
    .oneOf(
      [true],
      "Please check that you understand that D2T balance can only be used for trading and is non-withdrawable.",
    )
    .required(
      "Please check that you understand that D2T balance can only be used for trading and is non-withdrawable.",
    ),
});

function useBurnSCRD2TForm() {
  const { register, handleSubmit, errors, reset, setError } = useForm<BurnSCRD2TFormInfo>({
    mode: "all",
    reValidateMode: "onChange",
    resolver: yupResolver(burnSCRD2TValidationSchema),
    defaultValues: {
      convertBalance: false,
    },
  });

  return {
    register,
    handleSubmit,
    errors,
    reset,
    setError,
  };
}

export const TheViewScrToBrokerView: FC<TheViewScrToBrokerProps> = () => {
  const { t } = useTranslation();
  const { data: me, refetch: meRefetch, loading: meLoading } = useViewerQuery({ fetchPolicy: "network-only" });
  const { data: theViewData } = useTheViewDataViewQuery({ fetchPolicy: "network-only" });
  const [isReadMoreOpen, setIsReadMoreOpen] = useState(false);
  const [isBurnD2TMode, setIsBurnD2TMode] = useState(false);
  const [amountD2T, setAmountD2T] = useState("0");

  const [convertScrD2T] = useConvertScrToD2TMutation();

  const {
    register: burnSCRD2T,
    handleSubmit: handleBurnSCRD2TSubmit,
    errors: burnSCRD2TInputErrors,
    reset: resetBurnD2TScr,
    setError: setBurnSCRD2TError,
  } = useBurnSCRD2TForm();

  function getSCRsD2TAmount() {
    return theViewData?.getTheViewData?.maxAvailableBalance.toFixed(2) ?? 0;
  }

  function getScrD2TAmount() {
    if (theViewData?.getTheViewData?.maxAvailableBalance) {
      return (theViewData?.getTheViewData?.maxAvailableBalance / 1).toFixed(2);
    }

    return 0;
  }

  const totalSCRD2T = Math.min(
    me?.viewer?.scrCreditBalance ?? 0,
    theViewData?.getTheViewData?.maxAvailableBalance ?? 0,
  );

  const handlePercentageD2T = (percent: string) => {
    setAmountD2T(((totalSCRD2T * parseFloat(percent)) / 100).toFixed(2));
  };

  const handleBurnSCRFormD2TSubmit = handleBurnSCRD2TSubmit(async ({ totalAmount, name, email }) => {
    const test = (parseFloat(totalAmount) / 1).toFixed(2);
    if (parseFloat(test) < 25) {
      //   const myCustomFieldError: FieldError = {
      //     type: "required",
      //     message: t("Submitted D2T amount must be at least 25"),
      //     ref: undefined,
      //   };
      //   burnSCRD2TInputErrors.totalAmount = myCustomFieldError;
      setBurnSCRD2TError("totalAmount", {
        type: "manual",
        message: t("Submitted D2T amount must be at least 25"),
      });
      return;
    }
    try {
      const result = await convertScrD2T({
        variables: { totalAmount, name, email, convertBalance: false },
      });
      if (result.data) {
        toast.success(t("SCR burned to D2T successfully!"));
        resetBurnD2TScr();
        setAmountD2T("");
        meRefetch();
        // setIsWebinarsModalOpen(false);
      } else {
        const errorMessage = result?.errors?.[0]?.message;
        if (errorMessage) {
          const myCustomFieldError: FieldError = {
            type: "required",
            message: errorMessage,
            ref: undefined,
          };
          burnSCRD2TInputErrors.totalAmount = myCustomFieldError;

          setBurnSCRD2TError("totalAmount", {
            type: "manual",
            message: errorMessage || t("An unexpected error occurred"),
          });
        } else {
          toast.error(t("Something went wrong"));
        }
      }
    } catch (err: any) {
      if (err?.message === "Can not burn for others") {
        setBurnSCRD2TError("email", {
          type: "manual",
          message: err?.message || t("An unexpected error occurred"),
        });
      } else {
        setBurnSCRD2TError("totalAmount", {
          type: "manual",
          message: err?.message || t("An unexpected error occurred"),
        });
      }
      if (!showGraphqlValidationErrors(t, err) && err instanceof Error) {
        toast.error(err?.message);
      }
    }
  });

  return (
    <Section gutter={8} withSpace className={styles.wrap}>
      <Loader cover visibleOnFirstLoad={{ loading: meLoading }} />
      <div className={styles["header-row"]}>
        <Heading className={styles.mainTitle} gutter={8} level={4}>
          {t("Special SCR Burn For View Customers")}
        </Heading>
      </div>
      <h2 style={{ margin: "15px 0" }}>Amplify Your Trading Power!</h2>
      <p
        dangerouslySetInnerHTML={{
          __html: t(
            t(
              `We’re excited to introduce a powerful new opportunity with TAG Markets! Their Amplify 12x account lets traders maximize capital — boosting deposits 12X with no restrictions except a 10% drawdown limit — a perfect alternative to prop firms without the extra costs.<br/><br/><strong>Here's the Deal</strong>:<br/>Now, you can <strong>cover 50% of your deposit with SCR</strong>! Simply burn bonus SCR from your membership, and you’ll receive up to your full membership value in <strong>D2T promotional balance</strong> to use as part of your deposit — <strong>50% from SCR, 50% in fiat/crypto</strong>.<br/><br/><p>1 D2T gives 1 USD deposit in TAG Markets.</p><br/><br/>Get started today and take your trading to the next level! <strong><a href="{{url}}" rel="noopener noreferrer" target="_blank">Open your account</a></strong>`,
            ),
            { url: "https://aitech.ibportal.io/auth/register?e=jWUZXlM4mNCmBiI4M_6C-7xP4xKECwtmvRJtKO2JoGw&a=1" },
          ),
        }}
      />
      <p style={{ margin: "20px 0" }}>
        <Button
          onClick={() => setIsReadMoreOpen(true)}
          className={styles["access-product-link"]}
          borderRadius="MEDIUM"
          color={Color.DARK_BLUE}
          fontSize={16}
          width={300}
        >
          <span className={styles["link-label"]}>{t("How it works - see the steps!")}</span>
        </Button>
      </p>
      <ul>
        <li className={styles["dag-wrap"]}>
          <div className={styles["dag-section"]}>
            <div>
              <h3 className={styles.name}>Burn SCR and receive D2T</h3>
              <span style={{ color: "#d87333", fontSize: "12px" }}>Membership Burn</span>
            </div>
          </div>

          <Layout className={styles["dag-data"]}>
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("Your SCR balance")}
              value={`${me?.viewer?.scrCreditBalance} ${t("SCR")}`}
            />
            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("SCR You can burn")}
              value={`${getSCRsD2TAmount()} ${t("SCR")}`}
            />

            <DataFraction
              className={styles["data-fraction"]}
              icon={undefined}
              label={t("You receive")}
              value={`${getScrD2TAmount()} ${t("D2T")}`}
            />

            <Button
              className={styles["access-product-link"]}
              borderRadius="MEDIUM"
              color={Color.DARK_BLUE}
              fontSize={14}
              onClick={() => {
                setIsBurnD2TMode(true);
              }}
            >
              <span className={styles["link-label"]}>{t("Go to Burn")}</span>
            </Button>
          </Layout>
        </li>
      </ul>
      {me?.me?.meScrInfo?.burned &&
        me?.me?.meScrInfo?.burned?.map((burned, index) => (
          <div key={`${index}-term`} className={styles["term-grid"]}>
            <div>
              {burned.name}
              {burned.email && ` (${burned.email})`}
            </div>
            <div>
              <strong>D2T: </strong>
              {parseFloat(burned.scr).toFixed(2)}
            </div>
            <div>
              {burned.toBalance > 0 ? (
                "Converted as D2T Balance"
              ) : (
                <CopyToClipboard
                  iconColor
                  copyText={burned.txAddress ?? ""}
                  onClick={() => {
                    toast.success(t("TX hash copied"));
                  }}
                  icon={<IconBind fill="#ff871c" width={18} height={18} />}
                >
                  {burned.txAddress}
                </CopyToClipboard>
              )}
            </div>
            <div>
              <DateTime value={burned.createdAt} />
            </div>
            <div>
              <strong>Burned SCR:</strong> {burned.scr}
            </div>
          </div>
        ))}
      <Modal
        isOpen={isBurnD2TMode}
        close={() => {
          setIsBurnD2TMode(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("The View SCR Burn"),
          caption: t("Burn SCR and receive D2T"),
          label: t("The View SCR Burn"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div>
          {/* Burn SCR Content */}
          <h2 style={{ textAlign: "center", marginTop: "30px" }}>
            {t("Burn SCR and send D2T balance to TAG Markets")}
          </h2>
          <form onSubmit={handleBurnSCRFormD2TSubmit}>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("Full name registered in TAG Markets")}
                name="name"
                gutter={20}
                error={burnSCRD2TInputErrors.name}
                internalRef={burnSCRD2T}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="text"
                className={styles.field}
                label={t("E-mail registered in TAG Markets")}
                name="email"
                gutter={20}
                error={burnSCRD2TInputErrors.email}
                internalRef={burnSCRD2T}
                value={""}
                onChange={(e) => {
                  // setAmount(e);
                }}
              />
            </div>
            <div style={{ margin: "20px 0" }}>
              <Field
                type="number"
                className={styles.field}
                label={t("Total SCR to burn")}
                name="totalAmount"
                gutter={20}
                error={burnSCRD2TInputErrors.totalAmount}
                internalRef={burnSCRD2T}
                value={amountD2T}
                onChange={(e) => {
                  setAmountD2T(e);
                }}
              />
            </div>
            {/* <div style={{ margin: "20px 0" }}>
              <Field
                type="checkbox"
                className={styles.field}
                label={t("Convert to D2T balance")}
                name="convertBalance"
                gutter={20}
                checked={true}
                error={burnSCRD2TInputErrors.convertBalance}
                internalRef={burnSCRD2T}
              />
            </div> */}
            <p>
              <strong>You receive D2T (USD):</strong>{" "}
              <strong>{parseFloat(amountD2T ?? "0") > 0 ? (parseFloat(amountD2T ?? "0") / 1).toFixed(2) : "0"}</strong>
            </p>
            <p>
              <strong>Your special burn rate:</strong> <span style={{ fontWeight: "bold" }}>1:1</span>
            </p>
            <div style={{ display: "flex", justifyContent: "space-between", margin: "15px 0" }}>
              {["0", "25", "50", "75", "100"].map((percent) => (
                <button
                  type={"button"}
                  key={percent}
                  onClick={() => handlePercentageD2T(percent)}
                  style={{
                    flex: 1,
                    margin: "0 5px",
                    padding: "10px",
                    backgroundColor: "#007bff",
                    color: "#fff",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                >
                  {percent}%
                </button>
              ))}
            </div>
            <div style={{ margin: "20px 0" }}>
              <input
                type="range"
                min="0"
                max={totalSCRD2T}
                value={amountD2T}
                onChange={(e) => {
                  if (parseFloat(e.target.value) > totalSCRD2T) {
                    setAmountD2T(totalSCRD2T.toString());
                  } else {
                    setAmountD2T(parseFloat(e.target.value).toString());
                  }
                }}
                style={{ width: "100%" }}
              />
            </div>
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I have already opened TAG Markets account.")}
              name="accountOpened"
              gutter={20}
              error={burnSCRD2TInputErrors.accountOpened}
              internalRef={burnSCRD2T}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the burn and send is final and can not be reversed.")}
              name="acknowledgement"
              gutter={20}
              error={burnSCRD2TInputErrors.acknowledgement}
              internalRef={burnSCRD2T}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t("I acknowledge that the D2T balance can only be used for trading and is non-withdrawable.")}
              name="acknowledgementTrading"
              gutter={20}
              error={burnSCRD2TInputErrors.acknowledgementTrading}
              internalRef={burnSCRD2T}
            />
            <Field
              type="checkbox"
              className={styles.field}
              label={t(
                "I confirm that I have reviewed all details, and I understand the terms of the SCR burn and D2T balance conversion.",
              )}
              name="fconfirm"
              gutter={20}
              error={burnSCRD2TInputErrors.fconfirm}
              internalRef={burnSCRD2T}
            />
            <div style={{ textAlign: "center", width: "100%", alignItems: "center" }}>
              <Button
                className={styles["access-product-link"]}
                borderRadius="MEDIUM"
                color={Color.DARK_BLUE}
                fontSize={14}
                width={200}
              >
                <span className={styles["link-label"]}>{t("Burn SCR and Send D2T Balance")}</span>
              </Button>
            </div>
          </form>
        </div>
      </Modal>
      <Modal
        isOpen={isReadMoreOpen}
        close={() => {
          setIsReadMoreOpen(false);
        }}
        kind={ModalKind.PRIMARY}
        panelProps={{
          imagePath: "/images/crowd-icon.svg",
          width: PanelWidth.SMALL,
          title: t("The View SCR Burn"),
          caption: t("Burn SCR and receive D2T"),
          label: t("The View SCR Burn"),
          titleClassName: styles["modal-title"],
          captionClassName: styles["modal-caption"],
        }}
      >
        <div style={{ padding: "20px" }}>
          <h2 style={{ textAlign: "center", marginTop: "30px", marginBottom: "30px" }}>{t("What is TAG Markets?")}</h2>
          <p style={{ marginBottom: "20px" }}>
            TAG Markets is a regulated and transparent brokerage offering zero deposit and withdrawal fees, fast
            withdrawals, and a rapidly growing user base.
          </p>
          <h3>{t("How to Open Your Account and Make a Deposit:")}</h3>
          <ol>
            <li>
              {t("Open your Amplify 12X account with TAG Markets using this link:")}{" "}
              <a
                href="https://aitech.ibportal.io/auth/register?e=jWUZXlM4mNCmBiI4M_6C-7xP4xKECwtmvRJtKO2JoGw&a=1"
                target="_blank"
                rel="noreferrer"
              >
                {t("Sign up here")}
              </a>
            </li>
            <li style={{ marginTop: "20px" }}>
              {t(
                "Burn your SCR and receive D2T promotional balance. Each D2T = $1 in deposit value for your brokerage account.",
              )}
              <p style={{ marginTop: "10px", marginBottom: "10px" }}>
                Before you burn please check in the 12x Funded section in TAG Markets for the different account options
                (which total deposit you want to do) so you know how much USD deposit and how much D2T deposit you need
                for the account you wish to open.
              </p>
            </li>
            <li>
              {t("Go to Burn:")}
              <ul style={{ listStyleType: "none" }}>
                <li>{t("Enter your full name and email used for TAG Markets registration.")}</li>
                <li>{t("Choose the amount of SCR to burn.")}</li>
                <li>
                  {t(
                    "Send the received D2T balance to TAG Markets. This balance will cover max 50% of your total deposit.",
                  )}
                </li>
              </ul>
            </li>
            <li>
              {t("Complete your deposit:")}
              <br />
              {t("Make the remaining 50% deposit to your TAG Markets account via card, crypto, or wire transfer.")}
            </li>
            <li style={{ marginTop: "20px" }}>
              {t("Request for your 12x account:")}
              <br />
              <span
                dangerouslySetInnerHTML={{
                  __html: t(
                    `Once both deposits are done, go to (<a href="{{link}}" target="_blank" rel="noreferrer">{{link}}</a>) and fill in the form to request for the opening of a 12x account.`,
                    {
                      link: "https://form.jotform.com/tagforms2025/d2ttoken-accounts",
                    },
                  ),
                }}
              />
              <br />
              <br />
              <p>The form has the following steps to fill in:</p>{" "}
              <ol>
                <li>Fill in your full name</li>
                <li>Your e-mail address registered at TAG Markets</li>
                <li>USD deposit made at TAG Markets</li>
                <li>Value of burned tokens in USD (number of D2T)</li>
                <li>
                  Add the screenshot of tokens burned (after burning you see the burn info listed under the burn
                  section)
                </li>
                <li>
                  If you wish to upgrade - “skip” (this you can use if you want to increase the account balance later)
                </li>
                <li>Click “Send”</li>
              </ol>
              <br />
            </li>
            <li>{t("Now you should get a confirmation email of 12x account opening within 24h.")}</li>
            <li>
              {t("Start trading!")}
              <br />
              {t(
                "Once your 12x account is opened and fully funded, you are free to trade on your own, follow some strategies or even copy other experts - exactly how you wish.",
              )}
            </li>
          </ol>
          <div style={{ marginLeft: "40px" }}>
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              PS: In case you a looking for an automatic solution, there are some good strategies that have joined TAG
              Market’s social trading area. No extra charge, just subscribe, activate and system copies the trades
              automatically.
            </p>
            {/* <div style={{ marginTop: "10px", marginBottom: "10px", maxWidth: "500px" }}>
              <ol type="A">
                <li style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "5px" }}>
                  <span>Multistrategy (AI + human)</span>
                  <Link
                    color="DARK_BLUE"
                    borderRadius="SMALL"
                    weight="BOLD"
                    to={""}
                    externalUrl={"https://social.tagmarkets.com/portal/registration/subscription/84903/iTraderAl"}
                  >
                    {t("Start Copying")}
                  </Link>
                </li>
              </ol>
            </div> */}
            <p style={{ marginTop: "10px", marginBottom: "10px" }}>
              <a
                href="https://intercom.help/tag-markets/en/articles/10397021-how-to-link-your-account-to-a-private-copy"
                rel="noopener noreferrer"
                target="_blank"
              >
                An explanation how to link to a copy
              </a>
            </p>
            <p style={{ marginTop: "10px", marginBottom: "25px" }}>
              Trading account ID and password can be found in the email you received about your 12x account opening, or
              in the Account Summary section
            </p>
          </div>
          <h3>{t("Key rules for the Amplify 12X account:")}</h3>
          <ul style={{ marginLeft: "40px" }}>
            <li>{t("Minimum deposit is 50 USD - can be 25 from D2T balance and 25 from USD")}</li>
            <li>{t("Balance from D2T can be used for trading only")}</li>
            <li>{t("Initial deposit (via card/crypto/wire) can be withdrawn after 30 days")}</li>
            <li>{t("Withdrawing of initial deposit will result in an equal deduction from the D2T deposit")}</li>
            <li>{t("Trading profits can be withdrawn immediately")}</li>
            <li>{t("Genuine trading activity only - no overrisking, no gambling, no manipulation.")}</li>
            <li>{t("For full details, please review the terms of TAG Markets")}</li>
          </ul>

          <h3>{t("Good to know:")}</h3>
          <p style={{ marginTop: "10px", marginBottom: "25px" }}>
            All trades need to be closed (no open/ongoing trades) before performing any operations (such as adding
            capital, withdrawal, upgrading) with your trading account. You are allowed to withdraw profits immediately,
            but doing so will decrease the part of portfolio increased by the tokens. A good strategy is to wait with
            first withdrawal until portfolio has grown by the amount of your deposit. For example:
          </p>
          <ul style={{ marginLeft: "40px" }}>
            <li>{t("Deposit 500 USD + 500 D2T, this gives you 12 000 portfolio")}</li>
            <li>{t("Grow the portfolio to 13 000, earning 1000 profit (8.3% net growth on your portfolio)")}</li>
            <li>{t("Withdraw all capital from trading account")}</li>
            <li>{t("Use part for a new trading account and take part out as profit")}</li>
            <li>{t("Use 500 USD from your balance + burn 500 D2T again to make a new 12 000 portfolio")}</li>
            <li>{t("Repeat the cycle")}</li>
          </ul>
        </div>
      </Modal>
    </Section>
  );
};
